import { createTheme } from "@mui/material/styles";

/** Inter — body UI (matches Figma). */
export const FTC_FONT_STACK_SANS =
    '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

/** Cormorant Garamond — display headings (matches Figma). */
export const FTC_FONT_STACK_BRAND =
    '"Cormorant Garamond", Georgia, "Times New Roman", serif';

/**
 * Legacy export: system stack for non-dashboard surfaces that do not load Inter.
 * Dashboard uses {@link FTC_FONT_STACK_SANS}.
 */
export const FTC_FONT_STACK = FTC_FONT_STACK_SANS;

/**
 * Design tokens aligned with `Matches figma use this design.html`.
 * Primary actions: teal. Brand blue: nav badge, sidebar active, focus rings, tab indicator.
 */
export const FTC_COLORS = {
    // Teal — primary CTA (.bp)
    primary: "#00AEBA",
    primaryDark: "#008F99",
    primaryLight: "#D6F2F4",
    // Brand blue — badge, tabs, focus, sidebar active (T / TL / TD)
    brandBlue: "#005988",
    brandBlueLight: "#E0EFF7",
    brandBlueDark: "#00456B",
    // Neutrals
    text: "#1A2733",
    muted: "#5E6B78",
    border: "#DDE3EA",
    /** Muted placeholder / empty select text — matches booking “loved one” hint */
    inputPlaceholder: "#94a3b8",
    pageBg: "#F5F9FC",
    paper: "#ffffff",
    gl: "#EEF2F6",
    // Supporting tints (from HTML)
    accentBlueLight: "#EDF3FA",
    warnLight: "#F7ECD4",
    warnMedium: "#BA7517",
    warnDark: "#854F0B",
    warnText: "#b45309",
    errorLight: "#FAE8E6",
    errorDark: "#A32D2D",
    chipGrayBg: "#F5F9FC",
};

export const FTC_RADII = {
    sm: 10,
    lg: 16,
};

export const FTC_SHADOWS = {
    sm: "0 1px 2px rgba(0, 89, 136, 0.05)",
    md: "0 4px 16px rgba(0, 89, 136, 0.08)",
    lg: "0 12px 40px rgba(0, 89, 136, 0.14)",
    primaryButton: "0 1px 2px rgba(0, 174, 186, 0.25)",
    primaryButtonHover: "0 4px 12px rgba(0, 174, 186, 0.3)",
};

/** Outlined `TextField` / `Select` small row height — matches HTML `.field input` padding. */
export const FTC_FORM_FIELD_MIN_HEIGHT_PX = 46;
const formFieldHeightPx = FTC_FORM_FIELD_MIN_HEIGHT_PX;

/** MUI `FormLabel` / `InputLabel` root — same tokens as `formFieldLabel` (non-floating). */
const ftcMuiFieldLabelRoot = {
    fontFamily: FTC_FONT_STACK_SANS,
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: FTC_COLORS.muted,
    lineHeight: 1.2,
    "&.Mui-focused": {
        color: FTC_COLORS.brandBlue,
    },
    "&.Mui-error": {
        color: "#d32f2f",
    },
};

const dashTypographyBodyPrimary = {
    fontFamily: FTC_FONT_STACK_SANS,
    fontSize: 13,
    fontWeight: 400,
    lineHeight: 1.55,
    color: FTC_COLORS.text,
};

const dashTypographyBodyMuted = {
    fontFamily: FTC_FONT_STACK_SANS,
    fontSize: 13,
    fontWeight: 400,
    color: FTC_COLORS.muted,
};

/**
 * Reusable Typography `sx` snippets for dashboard pages (MUI `Typography` or `sx` spread).
 * Mirrors `Matches figma use this design.html` (e.g. `.pt`, `.ps`, `.dl`, `.di`, `.bid`, `.slbl`).
 *
 * Use `description` / `descriptionMuted` for standard paragraph copy app-wide on dashboard surfaces.
 */
export const dashboardTypographySx = {
    pageTitle: {
        fontFamily: FTC_FONT_STACK_BRAND,
        fontSize: { xs: "1.75rem", sm: "2rem", md: "34px" },
        fontWeight: 500,
        letterSpacing: "-0.01em",
        lineHeight: { xs: 1.15, md: 1.1 },
        color: FTC_COLORS.text,
        wordBreak: "break-word",
    },
    pageSubtitle: {
        fontFamily: FTC_FONT_STACK_SANS,
        fontSize: 13,
        fontWeight: 400,
        color: FTC_COLORS.muted,
        lineHeight: 1.5,
        marginTop: "6px",
        wordBreak: "break-word",
    },
    sectionTitle: {
        fontFamily: FTC_FONT_STACK_BRAND,
        fontSize: { xs: "1.125rem", md: "18px" },
        fontWeight: 600,
        letterSpacing: "-0.01em",
        lineHeight: 1.2,
        color: FTC_COLORS.text,
        wordBreak: "break-word",
    },
    modalTitle: {
        fontFamily: FTC_FONT_STACK_BRAND,
        fontSize: { xs: "1.25rem", sm: "1.5rem" },
        fontWeight: 500,
        letterSpacing: "-0.01em",
        lineHeight: 1.2,
        color: FTC_COLORS.text,
        wordBreak: "break-word",
    },
    navSectionLabel: {
        fontFamily: FTC_FONT_STACK_SANS,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        color: FTC_COLORS.muted,
    },
    labelUppercase: {
        fontFamily: FTC_FONT_STACK_SANS,
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: FTC_COLORS.muted,
    },
    /** Form field caption — HTML `.field label` (use with `FieldLabel` or `InputLabel`) */
    formFieldLabel: {
        display: "block",
        fontFamily: FTC_FONT_STACK_SANS,
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: FTC_COLORS.muted,
        lineHeight: 1.2,
        marginBottom: "0.4rem",
    },
    body: dashTypographyBodyPrimary,
    description: dashTypographyBodyPrimary,
    bodyMuted: dashTypographyBodyMuted,
    descriptionMuted: dashTypographyBodyMuted,
    /** Detail grid field label (`.dl`) — LOCATION, DURATION, etc. */
    detailGridLabel: {
        fontFamily: FTC_FONT_STACK_SANS,
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: FTC_COLORS.muted,
        lineHeight: 1.2,
        marginBottom: "2px",
    },
    /** Detail grid value text under label (`.di` content, 13px ink) */
    detailGridValue: {
        fontFamily: FTC_FONT_STACK_SANS,
        fontSize: 13,
        fontWeight: 400,
        lineHeight: 1.45,
        color: FTC_COLORS.text,
        wordBreak: "break-word",
    },
    detailGridValueMuted: {
        fontFamily: FTC_FONT_STACK_SANS,
        fontSize: 13,
        fontWeight: 400,
        lineHeight: 1.45,
        color: FTC_COLORS.muted,
        wordBreak: "break-word",
    },
    /** Booking id row (`.bid`) */
    bookingIdLine: {
        fontFamily: FTC_FONT_STACK_SANS,
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: FTC_COLORS.muted,
        lineHeight: 1.2,
    },
    /** Primary schedule line under booking id (inline 16px/600 in HTML cards) */
    bookingSchedulePrimary: {
        fontFamily: FTC_FONT_STACK_SANS,
        fontSize: 16,
        fontWeight: 600,
        color: FTC_COLORS.text,
        lineHeight: 1.35,
        marginTop: "2px",
    },
    /** Secondary line under schedule (e.g. time under loved-one name) */
    bookingScheduleSecondary: {
        fontFamily: FTC_FONT_STACK_SANS,
        fontSize: 12,
        fontWeight: 500,
        lineHeight: 1.35,
        color: FTC_COLORS.muted,
    },
    /** Footer block label — Caregiver, Caregiver on duty (`.slbl`) */
    footerSectionLabel: {
        fontFamily: FTC_FONT_STACK_SANS,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: FTC_COLORS.muted,
        lineHeight: 1.2,
        marginBottom: "0.4rem",
    },
    /** OTP / PIN helper blurb (11px muted in HTML `.otp-card`) */
    otpHelperText: {
        fontFamily: FTC_FONT_STACK_SANS,
        fontSize: 11,
        fontWeight: 400,
        lineHeight: 1.5,
        color: FTC_COLORS.muted,
    },
    /** OTP card title e.g. SESSION PIN (`.otp-label`) */
    otpCardTitle: {
        fontFamily: FTC_FONT_STACK_SANS,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        color: FTC_COLORS.brandBlueDark,
        lineHeight: 1.2,
        marginBottom: "5px",
    },
};

/** Full-page shell: cream background + soft radial washes (matches HTML `body` / `body::before`). */
export const dashboardPageShellSx = {
    position: "relative",
    width: "100%",
    maxWidth: "100%",
    overflowX: "hidden",
    backgroundColor: FTC_COLORS.pageBg,
    WebkitOverflowScrolling: "touch",
    "&::before": {
        content: '""',
        position: "fixed",
        inset: 0,
        background: [
            "radial-gradient(circle at 10% 0%, rgba(0, 174, 186, 0.05), transparent 45%)",
            "radial-gradient(circle at 90% 100%, rgba(0, 89, 136, 0.04), transparent 50%)",
        ].join(", "),
        pointerEvents: "none",
        zIndex: 0,
    },
    "& > *:not([data-dashboard-bottom-nav])": {
        position: "relative",
        zIndex: 1,
    },
};

const ftcPrimaryButtonComponents = {
    MuiButton: {
        defaultProps: {
            disableElevation: true,
        },
        styleOverrides: {
            root: {
                textTransform: "none",
                fontWeight: 600,
                borderRadius: FTC_RADII.sm,
                fontSize: "13px",
                lineHeight: 1.2,
                letterSpacing: "0.01em",
                fontFamily: FTC_FONT_STACK_SANS,
            },
            sizeMedium: {
                padding: "10px 20px",
            },
            containedPrimary: {
                backgroundColor: FTC_COLORS.primary,
                color: "#ffffff",
                boxShadow: FTC_SHADOWS.primaryButton,
                "@media (hover: hover) and (pointer: fine)": {
                    "&:hover": {
                        backgroundColor: FTC_COLORS.primaryDark,
                        boxShadow: FTC_SHADOWS.primaryButtonHover,
                        transform: "translateY(-1px)",
                    },
                },
                "&:disabled": {
                    opacity: 0.4,
                    transform: "none",
                    boxShadow: "none",
                },
            },
        },
    },
};

/** Outlined fields + labels shared by dashboard and CMS (add loved one / parent). */
const ftcSharedFormFieldComponents = {
    MuiTextField: {
        defaultProps: {
            variant: "outlined",
            size: "small",
            fullWidth: true,
        },
    },
    MuiFormLabel: {
        styleOverrides: {
            root: ftcMuiFieldLabelRoot,
        },
    },
    MuiInputLabel: {
        styleOverrides: {
            root: {
                ...ftcMuiFieldLabelRoot,
                "&.MuiInputLabel-shrink": {
                    letterSpacing: "0.06em",
                },
                "&.MuiInputLabel-outlined.MuiInputLabel-shrink": {
                    backgroundColor: FTC_COLORS.paper,
                    paddingLeft: "5px",
                    paddingRight: "5px",
                },
            },
        },
    },
    MuiOutlinedInput: {
        styleOverrides: {
            root: {
                backgroundColor: "#fff",
                borderRadius: FTC_RADII.sm,
                "& .MuiOutlinedInput-notchedOutline": {
                    borderWidth: "1.5px",
                    borderColor: FTC_COLORS.border,
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: FTC_COLORS.brandBlue,
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: FTC_COLORS.brandBlue,
                },
                "&.MuiInputBase-sizeSmall": {
                    minHeight: formFieldHeightPx,
                },
                "&.Mui-focused": {
                    boxShadow: `0 0 0 4px ${FTC_COLORS.brandBlueLight}`,
                },
            },
            input: {
                fontSize: 14,
                lineHeight: 1.5,
                // iOS Safari (iPhone + iPad) auto-zooms into any focused input
                // whose font-size is < 16px (which then triggers horizontal
                // scrolling). Bump to 16px on touch-sized widths; larger screens
                // keep the 14px design size.
                "@media (max-width:899.95px)": {
                    fontSize: 16,
                },
                "&::placeholder": {
                    color: FTC_COLORS.inputPlaceholder,
                    opacity: 1,
                },
                "&.MuiInputBase-inputSizeSmall": {
                    paddingTop: "11px",
                    paddingBottom: "11px",
                },
            },
        },
    },
    MuiSelect: {
        styleOverrides: {
            select: {
                fontSize: 14,
                display: "flex",
                alignItems: "center",
                "&.MuiInputBase-inputSizeSmall": {
                    minHeight: "auto",
                    paddingTop: "11px",
                    paddingBottom: "11px",
                },
            },
        },
    },
    MuiFormHelperText: {
        styleOverrides: {
            root: {
                marginLeft: 0,
                marginRight: 0,
                fontSize: 12,
                lineHeight: 1.45,
                color: FTC_COLORS.muted,
                fontFamily: FTC_FONT_STACK_SANS,
                "&.Mui-error": {
                    color: "#d32f2f",
                },
            },
        },
    },
};

/**
 * App-wide MUI theme (header, auth). Keeps teal primary; typography uses Inter when loaded.
 */
export const appRootTheme = createTheme({
    palette: {
        mode: "light",
        primary: {
            main: FTC_COLORS.primary,
            dark: FTC_COLORS.primaryDark,
            light: FTC_COLORS.primaryLight,
            contrastText: "#ffffff",
        },
    },
    shape: {
        borderRadius: FTC_RADII.sm,
    },
    typography: {
        fontFamily: FTC_FONT_STACK_SANS,
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    fontFamily: FTC_FONT_STACK_SANS,
                    fontFeatureSettings: '"ss01", "cv11"',
                },
            },
        },
        ...ftcPrimaryButtonComponents,
        ...ftcSharedFormFieldComponents,
    },
});

export const ftcDashboardTheme = createTheme({
    palette: {
        mode: "light",
        primary: {
            main: FTC_COLORS.primary,
            dark: FTC_COLORS.primaryDark,
            light: FTC_COLORS.primaryLight,
            contrastText: "#ffffff",
        },
        secondary: {
            main: FTC_COLORS.muted,
        },
        text: {
            primary: FTC_COLORS.text,
            secondary: FTC_COLORS.muted,
        },
        background: {
            default: FTC_COLORS.pageBg,
            paper: FTC_COLORS.paper,
        },
        divider: FTC_COLORS.border,
        error: {
            main: "#dc2626",
            dark: FTC_COLORS.errorDark,
        },
    },
    shape: {
        borderRadius: FTC_RADII.sm,
    },
    typography: {
        fontFamily: FTC_FONT_STACK_SANS,
        fontFeatureSettings: '"ss01", "cv11"',
        h1: {
            ...dashboardTypographySx.pageTitle,
        },
        h2: {
            ...dashboardTypographySx.modalTitle,
        },
        h3: {
            ...dashboardTypographySx.sectionTitle,
        },
        h4: {
            fontFamily: FTC_FONT_STACK_SANS,
            fontSize: "1.375rem",
            fontWeight: 600,
            lineHeight: 1.25,
            color: FTC_COLORS.text,
        },
        subtitle1: {
            ...dashboardTypographySx.pageSubtitle,
        },
        body1: {
            fontSize: 13,
            lineHeight: 1.55,
            color: FTC_COLORS.text,
        },
        body2: {
            fontSize: 13,
            color: FTC_COLORS.muted,
        },
        button: {
            fontFamily: FTC_FONT_STACK_SANS,
            fontWeight: 600,
            letterSpacing: "0.01em",
        },
        caption: {
            fontSize: "0.6875rem",
            fontWeight: 600,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: FTC_COLORS.muted,
        },
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    backgroundColor: FTC_COLORS.pageBg,
                    fontFamily: FTC_FONT_STACK_SANS,
                    fontFeatureSettings: '"ss01", "cv11"',
                },
            },
        },
        MuiButton: {
            defaultProps: {
                disableElevation: true,
            },
            styleOverrides: {
                root: {
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: `${FTC_RADII.sm}px`,
                    padding: "10px 20px",
                    fontSize: "13px",
                    lineHeight: 1.2,
                    minHeight: 40,
                    fontFamily: FTC_FONT_STACK_SANS,
                    letterSpacing: "0.01em",
                    transition: "all 0.18s ease",
                    "@media (max-width:600px)": {
                        padding: "8px 14px",
                        fontSize: "12px",
                        minHeight: 36,
                    },
                },
                sizeSmall: {
                    padding: "7px 14px",
                    fontSize: "12px",
                    minHeight: 32,
                },
                sizeLarge: {
                    padding: "13px 28px",
                    fontSize: "15px",
                    borderRadius: `${FTC_RADII.lg}px`,
                    minHeight: 48,
                },
                containedPrimary: {
                    backgroundColor: FTC_COLORS.primary,
                    color: "#ffffff",
                    boxShadow: FTC_SHADOWS.primaryButton,
                    "@media (hover: hover) and (pointer: fine)": {
                        "&:hover": {
                            backgroundColor: FTC_COLORS.primaryDark,
                            boxShadow: FTC_SHADOWS.primaryButtonHover,
                            transform: "translateY(-1px)",
                        },
                    },
                    "&:disabled": {
                        opacity: 0.4,
                        transform: "none",
                        boxShadow: "none",
                    },
                    "&:focus-visible": {
                        boxShadow: `0 0 0 4px ${FTC_COLORS.brandBlueLight}`,
                    },
                },
                outlined: {
                    borderWidth: "1.5px",
                    borderColor: FTC_COLORS.border,
                    color: FTC_COLORS.text,
                    fontWeight: 600,
                    "@media (hover: hover) and (pointer: fine)": {
                        "&:hover": {
                            borderWidth: "1.5px",
                            borderColor: FTC_COLORS.brandBlue,
                            color: FTC_COLORS.brandBlue,
                            backgroundColor: FTC_COLORS.brandBlueLight,
                        },
                    },
                },
                text: {
                    color: FTC_COLORS.muted,
                    fontWeight: 500,
                    padding: "6px 10px",
                    "@media (hover: hover) and (pointer: fine)": {
                        "&:hover": {
                            color: FTC_COLORS.text,
                            backgroundColor: "transparent",
                        },
                    },
                },
                outlinedError: {
                    borderWidth: "1.5px",
                    borderColor: "#f5b8b8",
                    color: FTC_COLORS.errorDark,
                    fontWeight: 600,
                    "@media (hover: hover) and (pointer: fine)": {
                        "&:hover": {
                            borderWidth: "1.5px",
                            borderColor: FTC_COLORS.errorDark,
                            backgroundColor: FTC_COLORS.errorLight,
                        },
                    },
                },
                textError: {
                    color: FTC_COLORS.errorDark,
                    fontWeight: 600,
                    "@media (hover: hover) and (pointer: fine)": {
                        "&:hover": {
                            backgroundColor: FTC_COLORS.errorLight,
                        },
                    },
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: FTC_RADII.lg,
                    border: `1px solid ${FTC_COLORS.border}`,
                    boxShadow: FTC_SHADOWS.sm,
                    transition: "all 0.2s ease",
                    maxWidth: "100%",
                    "@media (hover: hover) and (pointer: fine)": {
                        "&:hover": {
                            boxShadow: FTC_SHADOWS.md,
                            borderColor: "rgba(0, 89, 136, 0.2)",
                            transform: "translateY(-1px)",
                        },
                    },
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundImage: "none",
                },
            },
        },
        MuiTabs: {
            styleOverrides: {
                root: {
                    minHeight: 44,
                    borderBottom: `1px solid ${FTC_COLORS.border}`,
                },
                scrollButtons: {
                    "&.Mui-disabled": { opacity: 0.3 },
                },
                indicator: {
                    height: 2,
                    backgroundColor: FTC_COLORS.brandBlue,
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    textTransform: "none",
                    fontWeight: 500,
                    fontSize: "0.8125rem",
                    minHeight: 44,
                    minWidth: { xs: "auto", sm: 90 },
                    paddingLeft: { xs: 1, sm: 1.5 },
                    paddingRight: { xs: 1, sm: 1.5 },
                    color: FTC_COLORS.muted,
                    "&.Mui-selected": {
                        color: FTC_COLORS.brandBlueDark,
                        fontWeight: 600,
                    },
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    fontWeight: 500,
                    fontSize: "0.6875rem",
                },
            },
        },
        ...ftcSharedFormFieldComponents,
        MuiDialog: {
            styleOverrides: {
                paper: {
                    margin: { xs: "12px", sm: "32px" },
                    width: { xs: "calc(100% - 24px)", sm: undefined },
                    maxWidth: { xs: "calc(100% - 24px)", sm: undefined },
                    maxHeight: {
                        xs: "calc(100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 24px)",
                        sm: "calc(100% - 64px)",
                    },
                },
            },
        },
    },
});
